import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebaseConfig';
import { awaitRemoteConfirmation, requireConnectionForMutation } from './connectivityService';

export const ACCOUNT_ROLES = Object.freeze({
  OWNER: 'owner',
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer',
});

export const ACCOUNT_TYPES = Object.freeze({ PERSONAL: 'personal', FAMILY: 'family', SHARED: 'shared' });
export const MEMBERSHIP_TYPES = Object.freeze({ PERMANENT: 'permanent', INVITED: 'invited' });
export const MEMBER_RELATIONS = Object.freeze({ SPOUSE: 'spouse', CHILD: 'child', PARENT: 'parent', OTHER: 'other' });
const MANAGEABLE_ROLES = [ACCOUNT_ROLES.ADMIN, ACCOUNT_ROLES.EDITOR, ACCOUNT_ROLES.VIEWER];
const ACCOUNT_QUERY_BATCH_SIZE = 30;
const INVITE_TTL_MS = 24 * 60 * 60 * 1000;

const chunk = (items, size) => items.reduce((groups, item, index) => {
  const groupIndex = Math.floor(index / size);
  if (!groups[groupIndex]) groups[groupIndex] = [];
  groups[groupIndex].push(item);
  return groups;
}, []);

export const listUserAccounts = async (uid, firestoreDb = firestore) => {
  if (!uid) return [];
  const membershipsSnapshot = await getDocs(query(
    collectionGroup(firestoreDb, 'members'),
    where('uid', '==', uid)
  ));
  const memberships = membershipsSnapshot.docs.map((item) => item.data());
  return listAccountsForMemberships(memberships, firestoreDb);
};

export const listAccountsForMemberships = async (memberships = [], firestoreDb = firestore) => {
  const byAccountId = new Map(memberships.map((membership) => [membership.accountId, membership]));
  const accountIds = [...byAccountId.keys()].filter(Boolean);
  if (!accountIds.length) return [];
  const snapshots = await Promise.all(chunk(accountIds, ACCOUNT_QUERY_BATCH_SIZE).map((ids) => getDocs(query(
    collection(firestoreDb, 'accounts'), where(documentId(), 'in', ids)
  ))));
  return snapshots.flatMap((snapshot) => snapshot.docs.map((item) => ({
    id: item.id, ...item.data(), membership: byAccountId.get(item.id), role: byAccountId.get(item.id)?.role,
  })));
};

export const listAccountMembers = async (accountId, firestoreDb = firestore) => {
  if (!accountId) return [];
  const snapshot = await getDocs(collection(firestoreDb, 'accounts', accountId, 'members'));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};

export const listFamilyParticipants = async (accountId) => {
  if (!accountId) return [];
  const result = await httpsCallable(functions, 'getFamilyParticipants')({ accountId });
  return Array.isArray(result.data?.participants) ? result.data.participants : [];
};

export const observeAccountMembers = (accountId, accountType, next, fail, firestoreDb = firestore) => {
  if (!accountId) return () => {};
  let active = true;
  let generation = 0;
  const publish = () => {
    const request = ++generation;
    const loader = accountType === ACCOUNT_TYPES.FAMILY
      ? listFamilyParticipants(accountId)
      : listAccountMembers(accountId, firestoreDb);
    Promise.resolve(loader).then((members) => {
      if (active && request === generation) next(members);
    }).catch((error) => {
      if (active && request === generation) fail(error);
    });
  };
  const stop = onSnapshot(collection(firestoreDb, 'accounts', accountId, 'members'), publish, fail);
  return () => { active = false; stop(); };
};

const createSecureToken = () => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const REQUIRED_USER_FIELDS = ['uid', 'email', 'displayName', 'photoURL', 'createdAt', 'updatedAt'];
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

export const ensureUserDocument = async (user, firestoreDb = firestore) => {
  const userRef = doc(firestoreDb, 'users', user.uid);
  const snapshot = await getDoc(userRef);
  const authProfile = {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    photoURL: user.photoURL || '',
  };

  if (!snapshot.exists()) {
    await setDoc(userRef, {
      ...authProfile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return userRef;
  }

  const existing = snapshot.data() || {};
  if (hasOwn(existing, 'uid') && existing.uid !== user.uid) throw new Error('USER_UID_MISMATCH');
  const missingFields = REQUIRED_USER_FIELDS.filter((field) => !hasOwn(existing, field));

  if (missingFields.length > 0) {
    const repairPayload = {
      uid: hasOwn(existing, 'uid') ? existing.uid : authProfile.uid,
      email: hasOwn(existing, 'email') ? existing.email : authProfile.email,
      displayName: hasOwn(existing, 'displayName') ? existing.displayName : authProfile.displayName,
      photoURL: hasOwn(existing, 'photoURL') ? existing.photoURL : authProfile.photoURL,
      updatedAt: serverTimestamp(),
    };
    if (!hasOwn(existing, 'createdAt')) repairPayload.createdAt = serverTimestamp();

    if (process.env.NODE_ENV === 'development') {
      console.info('ensureUserDocument:legacy-repair', { missingFields });
    }
    await setDoc(userRef, repairPayload, { merge: true });
    return userRef;
  }

  await setDoc(userRef, { ...authProfile, updatedAt: serverTimestamp() }, { merge: true });
  return userRef;
};

export const getMembership = async (accountId, uid, firestoreDb = firestore) => {
  if (!accountId || !uid) return null;
  const snapshot = await getDoc(doc(firestoreDb, 'accounts', accountId, 'members', uid));
  return snapshot.exists() ? snapshot.data() : null;
};

export const selectCurrentAccount = async (uid, accountId, firestoreDb = firestore) => {
  requireConnectionForMutation();
  const membership = await getMembership(accountId, uid, firestoreDb);
  if (!membership) throw new Error('ACCOUNT_ACCESS_DENIED');

  await awaitRemoteConfirmation(setDoc(doc(firestoreDb, 'users', uid), {
    currentAccountId: accountId,
    updatedAt: serverTimestamp(),
  }, { merge: true }));

  return { accountId, role: membership.role };
};

export const createFinancialAccount = async ({ uid, name, type = ACCOUNT_TYPES.SHARED, displayName = '' }, firestoreDb = firestore) => {
  if (!uid || !name?.trim() || !Object.values(ACCOUNT_TYPES).includes(type)) throw new Error('INVALID_ACCOUNT');
  requireConnectionForMutation();

  const accountRef = doc(collection(firestoreDb, 'accounts'));
  const memberRef = doc(firestoreDb, 'accounts', accountRef.id, 'members', uid);
  const accountPayload = {
    ownerUid: uid,
    name: name.trim(),
    type,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    schemaVersion: 1,
  };
  const memberPayload = {
    uid,
    accountId: accountRef.id,
    role: ACCOUNT_ROLES.OWNER,
    relation: MEMBER_RELATIONS.OTHER,
    membershipType: MEMBERSHIP_TYPES.PERMANENT,
    ...(displayName.trim() ? { displayName: displayName.trim().slice(0, 120) } : {}),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const batch = writeBatch(firestoreDb);
  batch.set(accountRef, accountPayload);
  batch.set(memberRef, memberPayload);

  if (process.env.NODE_ENV === 'development') {
    console.info('createFinancialAccount:commit', {
      accountKeys: Object.keys(accountPayload).sort(),
      memberKeys: Object.keys(memberPayload).sort(),
      accountType: accountPayload.type,
      memberRole: memberPayload.role,
      isBatch: true,
    });
  }

  await awaitRemoteConfirmation(batch.commit());
  return { accountId: accountRef.id, role: ACCOUNT_ROLES.OWNER };
};

export const createAccountInvite = async ({
  accountId,
  createdBy,
  role = ACCOUNT_ROLES.EDITOR,
  relation = MEMBER_RELATIONS.OTHER,
  membershipType = MEMBERSHIP_TYPES.INVITED,
  displayName = '',
}, firestoreDb = firestore) => {
  requireConnectionForMutation();
  if (!MANAGEABLE_ROLES.includes(role)) throw new Error('INVALID_INVITE_ROLE');
  if (!Object.values(MEMBER_RELATIONS).includes(relation)) throw new Error('INVALID_MEMBER_RELATION');
  if (!Object.values(MEMBERSHIP_TYPES).includes(membershipType)) throw new Error('INVALID_MEMBERSHIP_TYPE');
  const normalizedDisplayName = typeof displayName === 'string' ? displayName.trim().slice(0, 120) : '';

  const token = createSecureToken();
  const inviteRef = doc(firestoreDb, 'invites', token);
  const expiresAt = new Date(Date.now() + INVITE_TTL_MS);

  await awaitRemoteConfirmation(setDoc(inviteRef, {
    accountId,
    createdBy,
    role,
    relation,
    membershipType,
    ...(normalizedDisplayName ? { displayName: normalizedDisplayName } : {}),
    status: 'pending',
    createdAt: serverTimestamp(),
    expiresAt,
  }));

  return { token, expiresAt };
};

export const createAccountInviteLink = async (options, firestoreDb = firestore) => {
  const { token } = await createAccountInvite(options, firestoreDb);
  const url = new URL(window.location.origin);
  url.pathname = '/login';
  url.searchParams.set('invite', token);
  return url.toString();
};

export const updateAccountMember = async ({ accountId, memberUid, role, relation }, firestoreDb = firestore) => {
  if (!accountId || !memberUid || !MANAGEABLE_ROLES.includes(role)) throw new Error('INVALID_MEMBER_ROLE');
  if (!Object.values(MEMBER_RELATIONS).includes(relation)) throw new Error('INVALID_MEMBER_RELATION');
  requireConnectionForMutation();
  await awaitRemoteConfirmation(updateDoc(doc(firestoreDb, 'accounts', accountId, 'members', memberUid), {
    role,
    relation,
    updatedAt: serverTimestamp(),
  }));
};

export const updateOwnMembershipDisplayName = async ({ accountId, uid, displayName }, firestoreDb = firestore) => {
  const normalized = typeof displayName === 'string' ? displayName.trim().slice(0, 120) : '';
  if (!accountId || !uid || !normalized) throw new Error('INVALID_MEMBER_DISPLAY_NAME');
  requireConnectionForMutation();
  await awaitRemoteConfirmation(updateDoc(doc(firestoreDb, 'accounts', accountId, 'members', uid), {
    displayName: normalized,
    updatedAt: serverTimestamp(),
  }));
};

export const removeAccountMember = async ({ accountId, memberUid }, firestoreDb = firestore) => {
  if (!accountId || !memberUid) throw new Error('INVALID_MEMBER');
  requireConnectionForMutation();
  await awaitRemoteConfirmation(deleteDoc(doc(firestoreDb, 'accounts', accountId, 'members', memberUid)));
};

export const consumeAccountInvite = async ({ token, user, firestoreDb = firestore }) => {
  if (!token) return null;
  requireConnectionForMutation();

  const inviteRef = doc(firestoreDb, 'invites', token);
  return awaitRemoteConfirmation(runTransaction(firestoreDb, async (transaction) => {
    const inviteSnapshot = await transaction.get(inviteRef);
    if (!inviteSnapshot.exists()) throw new Error('INVITE_NOT_FOUND');

    const invite = inviteSnapshot.data();
    if (invite.status === 'accepted' && invite.consumedBy === user.uid) {
      return { accountId: invite.accountId, role: invite.role, alreadyAccepted: true };
    }
    if (invite.status !== 'pending') throw new Error('INVITE_ALREADY_USED');
    if (!invite.expiresAt || invite.expiresAt.toMillis() <= Date.now()) throw new Error('INVITE_EXPIRED');

    const memberRef = doc(firestoreDb, 'accounts', invite.accountId, 'members', user.uid);
    const memberSnapshot = await transaction.get(memberRef);

    if (!memberSnapshot.exists()) {
      transaction.set(memberRef, {
        uid: user.uid,
        accountId: invite.accountId,
        role: invite.role,
        inviteId: token,
        relation: invite.relation || MEMBER_RELATIONS.OTHER,
        membershipType: invite.membershipType || MEMBERSHIP_TYPES.INVITED,
        ...(invite.displayName?.trim() ? { displayName: invite.displayName.trim().slice(0, 120) } : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    transaction.update(inviteRef, {
      status: 'accepted',
      consumedBy: user.uid,
      consumedAt: serverTimestamp(),
    });
    return {
      accountId: invite.accountId,
      role: memberSnapshot.exists() ? memberSnapshot.data().role : invite.role,
      alreadyAccepted: false,
    };
  }));
};
